/**
 * `GET /v1/audit-log` — the request shape.
 *
 * §5 lists this route and gives it no parameters of its own; what it inherits is the
 * conventions line — `?cursor=`, `?limit=`, opaque keyset cursors, `X-Next-Cursor`. So
 * this schema is cursor and limit and **nothing else**, and the absence is deliberate
 * rather than an unfinished first pass.
 *
 * ## Why no filters, when a log is the obvious thing to filter
 *
 * `?action=` and `?actor=` are both plainly useful and both would be this file's
 * invention, exactly as `sort`/`order` were c34's. c34's entry is the precedent and it
 * cuts the other way here: inventing a parameter *visibly* is right when the requirement
 * is real and the spec merely forgot to name a spelling — the dense table genuinely
 * required server-side sort. Nothing requires a filter here. U2's ruling asks for a log
 * *people can use to assess what is going on*, and the c36 view is "a minimal view on the
 * dense table" — a first page of a hundred rows, newest first, is that.
 *
 * The honest statement of the debt: the day this list is long enough to need filtering is
 * the day §5 should name the parameters, and that is the same conversation as the **gate**
 * §5 still carries (`Scale+`) which Phase 5 is ruled to decide. Two decisions about the
 * same route, and they should be made together rather than one now and one later.
 */
import { z } from "zod";
/** Rows per page when the caller does not say. Matches the dense table's cursor fetch. */
export const DEFAULT_AUDIT_PAGE_SIZE = 100;
/**
 * The ceiling. The same 500 the product list and §5's `bulk PATCH` bound use — one number
 * across this API rather than a second one that means the same thing.
 */
export const MAX_AUDIT_PAGE_SIZE = 500;
export const auditLogQuerySchema = z
    .object({
    /**
     * Opaque, and opaque is a contract rather than an encoding claim: a client may not
     * construct or parse one. Same rule as the product list — the schema takes an
     * arbitrary string and the op decides what it means, because validating the shape here
     * would make the encoding part of the public contract by accident.
     */
    cursor: z.string().min(1).optional(),
    limit: z.coerce
        .number("limit must be a whole number of rows.")
        .int("limit must be a whole number of rows.")
        .min(1, "limit must be at least 1.")
        .max(MAX_AUDIT_PAGE_SIZE, `limit is at most ${MAX_AUDIT_PAGE_SIZE}.`)
        .default(DEFAULT_AUDIT_PAGE_SIZE),
})
    .describe("GET /v1/audit-log — a keyset page of the organization's own trail.");
//# sourceMappingURL=audit-log.js.map